import { useCallback, useState, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useIdentity } from '../../identity/state/useIdentity';
import { Button } from '../../../shared/components/Button';
import { AccessGate } from '../components/AccessGate';
import { HubMenu } from '../components/HubMenu';
import { FileDropzone, FileRequirements } from '../components/intake';
import { validateZipFile } from '../fileValidation';
import { ErrorSummary, Pager, StatusBadge, StepHeading } from '../components/basics';
import { describeError, isConflict, isNotFound } from '../describeError';
import { formatDateTime, formatFileSize, formatMoney, shortId, sourceSystemLabel, STATUS_META } from '../format';
import { useAsyncData } from '../hooks/useAsyncData';
import { useIsMounted } from '../hooks/useIsMounted';
import { payrollHistoryImportGateway } from '../index';
import { HISTORY_PATH, batchStepPath, stepForBatch } from '../routes';
import type { BatchListQuery, BatchStatus, Capabilities } from '../types';
import '../payroll-history-import.css';

const PAGE_SIZE = 10;
const STATUS_OPTIONS = Object.keys(STATUS_META) as BatchStatus[];

interface Filters {
  status: BatchStatus | '';
  sourceSystem: string;
  from: string;
  to: string;
  importedBy: string;
}
const NO_FILTERS: Filters = { status: '', sourceSystem: '', from: '', to: '', importedBy: '' };

function ImportHistoryTable({ capabilities, companyName }: { capabilities: Capabilities; companyName: string }) {
  const [draft, setDraft] = useState<Filters>(NO_FILTERS);
  const [applied, setApplied] = useState<Filters>(NO_FILTERS);
  const [page, setPage] = useState(1);

  const load = useCallback(
    (signal: AbortSignal) => {
      const query: BatchListQuery = {
        status: applied.status,
        sourceSystem: applied.sourceSystem,
        from: applied.from,
        to: applied.to,
        importedBy: applied.importedBy,
        page,
        pageSize: PAGE_SIZE,
      };
      return payrollHistoryImportGateway.listBatches(query, signal);
    },
    [applied, page],
  );
  // Keep the previous page mounted while the next loads (stale-while-revalidate): unmounting the table and pager
  // would drop keyboard focus from the very button the user just pressed.
  const { loading, data, error, reload } = useAsyncData(load, { keepPrevious: true });

  const applyFilters = (event: FormEvent) => {
    event.preventDefault();
    setApplied(draft);
    setPage(1);
  };
  const clearFilters = () => {
    setDraft(NO_FILTERS);
    setApplied(NO_FILTERS);
    setPage(1);
  };
  const set = <K extends keyof Filters>(key: K, value: Filters[K]) => setDraft((d) => ({ ...d, [key]: value }));

  return (
    <section aria-labelledby="phi-history-heading">
      <h3 id="phi-history-heading" tabIndex={-1}>Import history</h3>
      <form className="phi-filters" onSubmit={applyFilters} aria-label="Filter import history">
        <div className="phi-field">
          <label htmlFor="phi-f-status">Status</label>
          <select id="phi-f-status" value={draft.status} onChange={(e) => set('status', e.target.value as BatchStatus | '')}>
            <option value="">All statuses</option>
            {STATUS_OPTIONS.map((s) => (
              <option key={s} value={s}>{STATUS_META[s].label}</option>
            ))}
          </select>
        </div>
        <div className="phi-field">
          <label htmlFor="phi-f-source">Source system</label>
          <select id="phi-f-source" value={draft.sourceSystem} onChange={(e) => set('sourceSystem', e.target.value)}>
            <option value="">All source systems</option>
            {capabilities.sourceSystems.map((s) => (
              <option key={s.id} value={s.id}>{sourceSystemLabel(s.id)}</option>
            ))}
          </select>
        </div>
        <div className="phi-field">
          <label htmlFor="phi-f-from">Uploaded from</label>
          <input id="phi-f-from" type="date" value={draft.from} onChange={(e) => set('from', e.target.value)} />
        </div>
        <div className="phi-field">
          <label htmlFor="phi-f-to">Uploaded to</label>
          <input id="phi-f-to" type="date" value={draft.to} onChange={(e) => set('to', e.target.value)} />
        </div>
        <div className="phi-field">
          <label htmlFor="phi-f-by">Uploaded by</label>
          <input id="phi-f-by" type="text" value={draft.importedBy} onChange={(e) => set('importedBy', e.target.value)} placeholder="Email" />
        </div>
        <button type="submit">Apply filters</button>
        <button type="button" className="phi-btn-secondary" onClick={clearFilters}>Clear filters</button>
      </form>

      {loading && !data && <p className="muted" role="status">Loading import history…</p>}
      {!loading && error !== undefined && (
        <div>
          <ErrorSummary message={describeError(error, 'Could not load the import history.')} />
          <button type="button" className="phi-btn-secondary" onClick={reload}>Try again</button>
        </div>
      )}
      {error === undefined && data && data.items.length === 0 && !loading && (
        <p>
          {JSON.stringify(applied) === JSON.stringify(NO_FILTERS) ? 'No payroll history imports yet.' : 'No imports match these filters.'}
        </p>
      )}
      {error === undefined && data && data.items.length > 0 && (
        <div aria-busy={loading}>
          <div className="table-scroll">
            <table className="phi-table-wide">
              <caption>Payroll history imports</caption>
              <thead>
                <tr>
                  <th scope="col">Import ID</th>
                  <th scope="col">Source platform</th>
                  <th scope="col">Company</th>
                  <th scope="col">Uploaded by</th>
                  <th scope="col">Uploaded date/time</th>
                  <th scope="col">Status</th>
                  <th scope="col" className="num">Employee count</th>
                  <th scope="col" className="num">Payroll-run count</th>
                  <th scope="col" className="num">Total gross</th>
                  <th scope="col" className="num">Total net</th>
                  <th scope="col" className="num">Warnings</th>
                  <th scope="col" className="num">Errors</th>
                  <th scope="col"><span className="phi-sr-only">Actions</span></th>
                </tr>
              </thead>
              <tbody>
                {data.items.map((b) => (
                  <tr key={b.id}>
                    <th scope="row" title={b.id}>{shortId(b.id)}</th>
                    <td>{sourceSystemLabel(b.sourceSystem)}</td>
                    <td>{companyName}</td>
                    <td>{b.createdByEmail}</td>
                    <td>{formatDateTime(b.createdAtUtc)}</td>
                    <td><StatusBadge status={b.status} /></td>
                    <td className="num">{b.employeeCount}</td>
                    <td className="num">{b.payrollRunCount}</td>
                    <td className="num">{formatMoney(b.totalGross)}</td>
                    <td className="num">{formatMoney(b.totalNet)}</td>
                    <td className="num">{b.warningCount}</td>
                    <td className="num">{b.errorCount}</td>
                    <td>
                      <Link to={batchStepPath(b.id, stepForBatch(b))} className="phi-btn-secondary" aria-label={`Open import ${shortId(b.id)}`}>
                        Open
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <Pager page={data.page} pageSize={data.pageSize} totalCount={data.totalCount} onPage={setPage} label="Import history pages" />
        </div>
      )}
    </section>
  );
}

/** Landing-page quick start for the Escochex Payroll package: drop or choose the .zip to start an import straight
 * away (start batch -> upload -> validate). Any other source - the EPN Excel template, or a spreadsheet or CSV
 * from another system - starts from "Import Payroll History". */
function QuickStart({ capabilities }: { capabilities: Capabilities }) {
  const navigate = useNavigate();
  const mounted = useIsMounted();
  const escochex = capabilities.providers.find((p) => p.id === 'EscochexPayroll');
  const sourceSystem = escochex?.sourceSystem ?? capabilities.sourceSystems.find((s) => s.id === 'EscochexPayroll')?.id ?? '';
  const [file, setFile] = useState<File | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [working, setWorking] = useState(false);
  // A Draft created by an attempt whose upload then failed is reused by the retry instead of orphaning another.
  const [draft, setDraft] = useState<{ id: string } | null>(null);

  if (!sourceSystem) return null;

  const choose = (files: FileList | null) => {
    const picked = files?.[0];
    if (!picked) return;
    const problem = validateZipFile(picked, capabilities.maxUploadBytes);
    setError(problem);
    setFile(problem ? null : picked);
  };

  const start = async () => {
    if (!file) return;
    setWorking(true);
    setError(null);
    try {
      let batchId = draft ? draft.id : null;
      if (!batchId) {
        const created = await payrollHistoryImportGateway.startImport(sourceSystem, escochex?.id);
        batchId = created.id;
        if (mounted.current) setDraft({ id: created.id });
      }
      const uploaded = await payrollHistoryImportGateway.uploadFile(batchId, file);
      if (!mounted.current) return;
      navigate(batchStepPath(uploaded.id, stepForBatch(uploaded)));
    } catch (err) {
      if (!mounted.current) return;
      setError(describeError(err, 'The upload failed.'));
      setWorking(false);
      // The Draft is kept for the retry unless the server says it can no longer take a file.
      if (isNotFound(err) || (isConflict(err) && (err as { code?: string }).code === 'INVALID_STATE')) setDraft(null);
    }
  };

  return (
    <section aria-labelledby="phi-quickstart-heading" className="phi-notice">
      <h3 id="phi-quickstart-heading">Start an import</h3>
      <p>
        Have an Escochex Payroll package? Upload it here. For the EPN Excel template, or a spreadsheet or CSV from another
        system, choose Import Payroll History.
      </p>
      <FileRequirements maxUploadBytes={capabilities.maxUploadBytes} />
      {error && <ErrorSummary message={error} />}
      <FileDropzone disabled={working} onFiles={choose} inputLabel="Payroll history package to import (.zip)" />
      {file && (
        <p>
          Selected: <strong>{file.name}</strong> ({formatFileSize(file.size)})
        </p>
      )}
      <div className="phi-actions">
        <Button type="button" disabled={!file} loading={working} loadingLabel="Uploading and validating…" onClick={() => void start()}>
          Upload and validate
        </Button>
      </div>
    </section>
  );
}

export function ImportPayrollDataPage() {
  const { me } = useIdentity();
  const companyName = me?.memberships.find((m) => m.companyId === me.activeCompanyId)?.companyName ?? '—';

  return (
    <section className="card card--wide phi">
      <AccessGate require="read">
        {(capabilities) => (
          <>
            <StepHeading>Import Payroll Data</StepHeading>
            <p className="phi-lead">
              Bring historical payroll records into Escochex Payroll Next using a secure export from your previous payroll system.
            </p>
            <p className="muted">
              Imported payroll is kept as read-only history for reference. An import never starts payments, tax deposits or filings, or accounting entries.
            </p>
            <HubMenu capabilities={capabilities} />
            <div className="phi-actions">
              <Link to={HISTORY_PATH}>View imported payroll history</Link>
            </div>
            {capabilities.canCreate && capabilities.enabled && <QuickStart capabilities={capabilities} />}
            <ImportHistoryTable capabilities={capabilities} companyName={companyName} />
          </>
        )}
      </AccessGate>
    </section>
  );
}
